import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Building2, 
  DollarSign, 
  Calendar,
  Users,
  TrendingUp,
  FileText,
  Brain,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  Sparkles,
  Calculator,
  MapPin,
  CreditCard
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";

const ApplicationReview = () => {
  const { applicationId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [alternativeData, setAlternativeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assessing, setAssessing] = useState(false);
  const [calculatingScore, setCalculatingScore] = useState(false);
  const [submittingDecision, setSubmittingDecision] = useState(false);
  const [decisionDialog, setDecisionDialog] = useState(false);
  const [decisionType, setDecisionType] = useState("");
  const [decisionData, setDecisionData] = useState({
    approved_amount: "",
    interest_rate: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [applicationId]);

  const fetchData = async () => {
    try {
      const [appRes, altDataRes] = await Promise.all([
        axios.get(`${API}/loans/${applicationId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/alternative-data/${applicationId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setApplication(appRes.data);
      setAlternativeData(altDataRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load application");
    } finally {
      setLoading(false);
    }
  };

  const handleAssessRisk = async () => {
    setAssessing(true);
    try {
      const response = await axios.post(
        `${API}/loans/${applicationId}/assess-risk`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setApplication(prev => ({
        ...prev,
        risk_score: response.data.risk_score,
        risk_assessment: response.data.assessment
      }));
      
      toast.success("Risk assessment completed!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assess risk");
    } finally {
      setAssessing(false);
    }
  };

  const handleCalculateCreditScore = async () => {
    setCalculatingScore(true);
    try {
      const response = await axios.post(
        `${API}/credit-score/calculate/${applicationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setApplication(prev => ({
        ...prev,
        credit_score: response.data.score,
        credit_score_details: response.data,
        risk_level: response.data.risk_level
      }));
      
      toast.success(`Credit score calculated: ${response.data.score}/100`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to calculate credit score");
    } finally {
      setCalculatingScore(false);
    }
  };

  const handleDecision = async () => {
    setSubmittingDecision(true);
    try {
      await axios.post(
        `${API}/loans/${applicationId}/decision`,
        {
          application_id: applicationId,
          decision: decisionType,
          approved_amount: decisionType === "approved" ? parseFloat(decisionData.approved_amount) : null,
          interest_rate: decisionType === "approved" ? parseFloat(decisionData.interest_rate) : null,
          notes: decisionData.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Application ${decisionType}!`);
      setDecisionDialog(false);
      navigate("/officer/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit decision");
    } finally {
      setSubmittingDecision(false);
    }
  };

  const openDecisionDialog = (type) => {
    setDecisionType(type);
    if (type === "approved" && application) {
      setDecisionData(prev => ({
        ...prev,
        approved_amount: application.amount.toString()
      }));
    }
    setDecisionDialog(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Approved</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Rejected</Badge>;
      case "pending_review":
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Under Review</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>;
    }
  };

  const getRiskColor = (score) => {
    if (score >= 70) return "text-emerald-600";
    if (score >= 50) return "text-amber-600";
    return "text-red-600";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 animate-spin text-[#0A2540]" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Application Not Found</h2>
          <Link to="/officer/dashboard">
            <Button className="mt-4">Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="application-review">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link 
                to="/officer/dashboard" 
                className="flex items-center gap-2 text-gray-600 hover:text-[#0A2540]"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Link>
            </div>
            <img src={LOGO_URL} alt="Tzur Finances" className="h-8" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-[#0A2540]">Application Review</h1>
              {getStatusBadge(application.status)}
            </div>
            <p className="text-gray-500 mt-1 font-mono text-sm">{application.application_id}</p>
          </div>
          
          {application.status === "pending" && (
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => openDecisionDialog("rejected")}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="reject-btn"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button
                onClick={() => openDecisionDialog("approved")}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="approve-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Loan Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-[#0A2540] flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Loan Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-gray-500">Loan Type</p>
                    <p className="font-semibold text-[#0A2540]">{application.loan_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Amount Requested</p>
                    <p className="font-mono font-bold text-xl text-[#0A2540]">
                      {formatCurrency(application.amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Term</p>
                    <p className="font-semibold text-[#0A2540]">{application.term_months} months</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Applied</p>
                    <p className="font-medium text-gray-700">{formatDate(application.created_at)}</p>
                  </div>
                </div>
                
                <Separator className="my-6" />
                
                <div>
                  <p className="text-sm text-gray-500 mb-2">Purpose</p>
                  <p className="text-gray-700">{application.purpose}</p>
                </div>
              </CardContent>
            </Card>

            {/* Business Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-[#0A2540] flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Business Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-gray-500">Business Name</p>
                    <p className="font-semibold text-[#0A2540]">{application.business_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">License Number</p>
                    <p className="font-medium text-gray-700">{application.license_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Business Type</p>
                    <p className="font-medium text-gray-700">{application.business_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Years in Business</p>
                    <p className="font-medium text-gray-700">{application.years_in_business} years</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Employees</p>
                    <p className="font-medium text-gray-700">{application.employees_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="font-medium text-gray-700 text-sm">
                      {application.business_address && `${application.business_address}, `}
                      {application.city}{application.state && `, ${application.state}`}
                    </p>
                  </div>
                </div>
                
                {application.directors && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Directors/Owners</p>
                      <p className="text-gray-700">{application.directors}</p>
                    </div>
                  </>
                )}
                
                {application.security_type && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Security/Collateral</p>
                      <p className="font-medium text-[#0A2540]">{application.security_type}</p>
                      {application.security_description && (
                        <p className="text-sm text-gray-600 mt-1">{application.security_description}</p>
                      )}
                    </div>
                  </>
                )}
                
                <Separator className="my-4" />
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Annual Revenue</p>
                    <p className="font-mono font-semibold text-[#0A2540]">
                      {formatCurrency(application.annual_revenue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Monthly Expenses</p>
                    <p className="font-mono font-semibold text-gray-700">
                      {formatCurrency(application.monthly_expenses)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Existing Debt</p>
                    <p className="font-mono font-semibold text-gray-700">
                      {formatCurrency(application.existing_debt || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Alternative Data */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-[#0A2540] flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Alternative Data
                </CardTitle>
                <CardDescription>
                  Additional data submitted by the applicant
                </CardDescription>
              </CardHeader>
              <CardContent>
                {alternativeData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p>No alternative data submitted yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {alternativeData.map((data, index) => (
                      <div 
                        key={data.data_id || index}
                        className="p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary">
                            {data.data_type.replace('_', ' ')}
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {formatDate(data.created_at)}
                          </span>
                        </div>
                        <p className="text-gray-700">{data.data_summary}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Risk Assessment */}
          <div className="space-y-6">
            {/* Applicant Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-[#0A2540] flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Applicant
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">{application.user_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-sm">{application.user_email}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Credit Score (Internal Model) */}
            <Card className="border-2 border-amber-200 bg-amber-50/50">
              <CardHeader>
                <CardTitle className="text-lg text-[#0A2540] flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Credit Score
                </CardTitle>
                <CardDescription>Internal scoring model</CardDescription>
              </CardHeader>
              <CardContent>
                {application.credit_score ? (
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className={`text-5xl font-bold font-mono ${getRiskColor(application.credit_score)}`}>
                        {application.credit_score}
                      </p>
                      <p className="text-sm text-gray-500">out of 100</p>
                      <Badge className={`mt-2 ${
                        application.risk_level === 'LOW' ? 'bg-emerald-100 text-emerald-700' :
                        application.risk_level === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {application.risk_level} RISK
                      </Badge>
                      <Progress value={application.credit_score} className="mt-3 h-3" />
                    </div>
                    
                    {application.credit_score_details?.factors && (
                      <>
                        <Separator />
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Score Factors</p>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {application.credit_score_details.factors.map((factor, idx) => (
                              <div key={idx} className="flex justify-between items-center text-sm p-2 bg-white rounded">
                                <span className="text-gray-600">{factor.factor}</span>
                                <span className={`font-mono font-semibold ${
                                  factor.impact.startsWith('+') ? 'text-emerald-600' :
                                  factor.impact.startsWith('-') ? 'text-red-600' : 'text-gray-500'
                                }`}>
                                  {factor.impact}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={handleCalculateCreditScore}
                      disabled={calculatingScore}
                    >
                      {calculatingScore ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Calculator className="h-4 w-4 mr-2" />
                      )}
                      Recalculate Score
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Calculator className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">No credit score yet</p>
                    <Button 
                      onClick={handleCalculateCreditScore}
                      disabled={calculatingScore}
                      className="bg-amber-500 hover:bg-amber-600 text-[#0A2540]"
                      data-testid="calculate-score-btn"
                    >
                      {calculatingScore ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Calculating...
                        </>
                      ) : (
                        <>
                          <Calculator className="h-4 w-4 mr-2" />
                          Calculate Credit Score
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Risk Assessment */}
            <Card className="border-2 border-[#0A2540]/10">
              <CardHeader>
                <CardTitle className="text-lg text-[#0A2540] flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  AI Risk Assessment
                </CardTitle>
                <CardDescription>GPT-powered analysis</CardDescription>
              </CardHeader>
              <CardContent>
                {application.risk_score ? (
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-2">AI Risk Score</p>
                      <p className={`text-4xl font-bold font-mono ${getRiskColor(application.risk_score)}`}>
                        {application.risk_score}
                      </p>
                      <p className="text-sm text-gray-400">out of 100</p>
                      <Progress 
                        value={application.risk_score} 
                        className="mt-3 h-2"
                      />
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Assessment Details</p>
                      <ScrollArea className="h-48">
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">
                          {application.risk_assessment}
                        </p>
                      </ScrollArea>
                    </div>
                    
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={handleAssessRisk}
                      disabled={assessing}
                      data-testid="reassess-btn"
                    >
                      {assessing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="h-4 w-4 mr-2" />
                      )}
                      Re-assess with AI
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Brain className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">No AI assessment yet</p>
                    <Button 
                      onClick={handleAssessRisk}
                      disabled={assessing}
                      className="bg-[#0A2540]"
                      data-testid="assess-risk-btn"
                    >
                      {assessing ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Run AI Assessment
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-[#0A2540] flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Financial Ratios
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-500">Debt-to-Revenue</span>
                  <span className="font-mono font-semibold">
                    {((application.existing_debt || 0) / application.annual_revenue * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Loan-to-Revenue</span>
                  <span className="font-mono font-semibold">
                    {(application.amount / application.annual_revenue * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Monthly Coverage</span>
                  <span className="font-mono font-semibold">
                    {(application.annual_revenue / 12 / application.monthly_expenses).toFixed(2)}x
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Decision Dialog */}
      <Dialog open={decisionDialog} onOpenChange={setDecisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decisionType === "approved" ? "Approve Application" : "Reject Application"}
            </DialogTitle>
            <DialogDescription>
              {decisionType === "approved" 
                ? "Specify the approved terms for this loan application."
                : "Provide a reason for rejecting this application."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {decisionType === "approved" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="approved_amount">Approved Amount ($)</Label>
                  <Input
                    id="approved_amount"
                    type="number"
                    value={decisionData.approved_amount}
                    onChange={(e) => setDecisionData(prev => ({ ...prev, approved_amount: e.target.value }))}
                    data-testid="approved-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="interest_rate">Interest Rate (%)</Label>
                  <Input
                    id="interest_rate"
                    type="number"
                    step="0.1"
                    placeholder="12.5"
                    value={decisionData.interest_rate}
                    onChange={(e) => setDecisionData(prev => ({ ...prev, interest_rate: e.target.value }))}
                    data-testid="interest-rate-input"
                  />
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="notes">
                {decisionType === "approved" ? "Notes" : "Rejection Reason"}
              </Label>
              <Textarea
                id="notes"
                placeholder={decisionType === "approved" 
                  ? "Add any notes about this approval..."
                  : "Explain why this application is being rejected..."}
                value={decisionData.notes}
                onChange={(e) => setDecisionData(prev => ({ ...prev, notes: e.target.value }))}
                data-testid="decision-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDecision}
              disabled={submittingDecision || (decisionType === "approved" && (!decisionData.approved_amount || !decisionData.interest_rate))}
              className={decisionType === "approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="confirm-decision-btn"
            >
              {submittingDecision ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                decisionType === "approved" ? "Approve" : "Reject"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicationReview;
